import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { logOut } from '../../store/authSlice';
import { resetFilters, resetPagination } from '../../../players/store/actions';
import { setUnreadCount } from '../../../messages/store/conversationsSlice';
import { setAdminUnreadCount } from '../../../messages/store/adminConversationsSlice';
import {
  fetchChecklistNotifications,
  markMyTaskRead,
} from '../../../checklist/store/checklistSlice';

import '../adminSidebar/AdminSidebar.scss';

const STEP_LABELS = { 1: 'Krok 1', 2: 'Krok 2', 3: 'Krok 3' };

const formatRelative = (dateStr) => {
  const diffMin = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diffMin < 1) return 'Teraz';
  if (diffMin < 60) return `${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} godz.`;
  return `${Math.floor(diffH / 24)} dni`;
};

const CoachBar = ({ userName, userLastName, setIsAuthorized, userRole }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const unreadCount = useSelector(({ conversations }) => conversations.unreadCount);
  const adminUnreadCount = useSelector(({ adminConversations }) => adminConversations.adminUnreadCount);
  const checklistUnreadCount = useSelector(({ checklist }) => checklist.checklistUnreadCount);
  const notifications = useSelector(({ checklist }) => checklist.notifications);
  const recentlyRead = useSelector(({ checklist }) => checklist.recentlyRead);
  const [notifOpen, setNotifOpen] = useState(false);
  const [listTop, setListTop] = useState(0);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!notifOpen) return undefined;
    const handleClickOutside = e => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const handleTabClick = () => {
    dispatch(resetPagination());
    dispatch(resetFilters());
  };

  const handleLogout = () => {
    setIsAuthorized(false);
    dispatch(logOut(history, { key: 'authorization', storage: window.localStorage }));
  };

  const displayName = userName
    ? userName.charAt(0).toUpperCase() + userName.slice(1)
    : '';

  const totalUnread = checklistUnreadCount;

  const handleBellClick = () => {
    if (!notifOpen) {
      dispatch(fetchChecklistNotifications());
      if (bellRef.current) {
        setListTop(bellRef.current.getBoundingClientRect().bottom);
      }
    }
    setNotifOpen(prev => !prev);
  };

  const handleNotifClick = (n) => {
    dispatch(markMyTaskRead(n.taskId));
    history.push(`/player/checklist?task=${n.taskId}`);
    setNotifOpen(false);
  };

  if (userRole === 'admin') return null;

  if (userRole === 'player') {
    return (
      <aside className="admin-sidebar">
        <div className="admin-sidebar__sticky">
          <div className="admin-sidebar__greeting">
            Moje konto{displayName || userLastName ? ` – ${displayName}${userLastName ? ` ${userLastName.charAt(0).toUpperCase() + userLastName.slice(1)}` : ''}` : ''}
          </div>

          {/* Bell / notifications */}
          <div className="admin-sidebar__notif-wrap">
            <button
              ref={bellRef}
              type="button"
              className={`admin-sidebar__link admin-sidebar__bell-btn${notifOpen ? ' admin-sidebar__bell-btn--open' : ''}`}
              onClick={handleBellClick}
            >
              <i className="fas fa-bell" />
              Powiadomienia
              {totalUnread > 0 && !notifOpen && (
                <span className="admin-sidebar__badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
              )}
              <i className={`fas fa-chevron-${notifOpen ? 'up' : 'down'} admin-sidebar__bell-chevron`} />
            </button>

            {notifOpen && ReactDOM.createPortal(
              <div
                ref={dropdownRef}
                className="admin-sidebar__notif-list"
                style={{ position: 'fixed', top: listTop, left: '8px', width: '300px', zIndex: 99999 }}
              >
                <div className="admin-sidebar__notif-header">
                  <span className="admin-sidebar__notif-title">
                    <i className="fas fa-bell" />
                    Powiadomienia
                  </span>
                  {checklistUnreadCount > 0 && (
                    <span className="admin-sidebar__notif-count">{checklistUnreadCount > 99 ? '99+' : checklistUnreadCount}</span>
                  )}
                </div>
                {notifications.length === 0 && recentlyRead.length === 0 ? (
                  <div className="admin-sidebar__notif-empty">
                    <i className="fas fa-check-circle" />
                    Brak nowych powiadomień
                  </div>
                ) : (
                  <>
                    {notifications.map((n, i) => (
                      <button
                        // eslint-disable-next-line react/no-array-index-key
                        key={`unread-${n.taskId}-${i}`}
                        type="button"
                        className="admin-sidebar__notif-item"
                        onClick={() => handleNotifClick(n)}
                      >
                        <div className="admin-sidebar__notif-meta">
                          <span className="admin-sidebar__notif-author">
                            <i className="fas fa-user-circle" style={{ marginRight: '5px', color: '#94a3b8' }} />
                            {n.author}
                          </span>
                          <span className="admin-sidebar__notif-time">{formatRelative(n.createdAt)}</span>
                        </div>
                        <div className="admin-sidebar__notif-task">
                          <span className="admin-sidebar__notif-step">{STEP_LABELS[n.step]}</span>
                          {n.taskLabel}
                        </div>
                        {n.preview && (
                          <div className="admin-sidebar__notif-preview">&ldquo;{n.preview}&rdquo;</div>
                        )}
                      </button>
                    ))}
                    {recentlyRead.length > 0 && (
                      <>
                        <div className="admin-sidebar__notif-divider">
                          <span>Ostatnio przeczytane</span>
                        </div>
                        {recentlyRead.map((n, i) => (
                          <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={`read-${n.taskId}-${i}`}
                            type="button"
                            className="admin-sidebar__notif-item admin-sidebar__notif-item--read"
                            onClick={() => handleNotifClick(n)}
                          >
                            <div className="admin-sidebar__notif-meta">
                              <span className="admin-sidebar__notif-author">
                                <i className="fas fa-user-circle" style={{ marginRight: '5px', color: '#94a3b8' }} />
                                {n.author}
                              </span>
                              <span className="admin-sidebar__notif-time">{formatRelative(n.createdAt)}</span>
                            </div>
                            <div className="admin-sidebar__notif-task">
                              <span className="admin-sidebar__notif-step">{STEP_LABELS[n.step]}</span>
                              {n.taskLabel}
                            </div>
                            {n.preview && (
                              <div className="admin-sidebar__notif-preview">&ldquo;{n.preview}&rdquo;</div>
                            )}
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>,
              document.body
            )}
          </div>

          <nav className="admin-sidebar__nav">
            <NavLink
              to="/player/inbox"
              className="admin-sidebar__link"
              activeClassName="admin-sidebar__link--active"
              onClick={() => dispatch(setUnreadCount(0))}
            >
              <i className="fas fa-envelope" />
              Wiadomości
              {unreadCount > 0 && (
                <span className="admin-sidebar__badge">{unreadCount}</span>
              )}
            </NavLink>
            <NavLink
              to="/player/checklist"
              className="admin-sidebar__link"
              activeClassName="admin-sidebar__link--active"
            >
              <i className="fas fa-tasks" />
              Lista zadań
            </NavLink>
          </nav>
          <div className="admin-sidebar__footer">
            <NavLink
              to="/finish-profile"
              className="admin-sidebar__link"
              activeClassName="admin-sidebar__link--active"
            >
              <i className="fas fa-cog" />
              Ustawienia
            </NavLink>
            <button
              type="button"
              className="admin-sidebar__link admin-sidebar__link--logout"
              onClick={handleLogout}
            >
              <i className="fas fa-sign-out-alt" />
              Wyloguj
            </button>
          </div>
        </div>
      </aside>
    );
  }

  // Coach
  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar__sticky">
        <div className="admin-sidebar__greeting">
          Hi, {displayName || 'Coach'}!
        </div>
        <nav className="admin-sidebar__nav">
          <NavLink
            to="/available-players"
            className="admin-sidebar__link"
            activeClassName="admin-sidebar__link--active"
            onClick={handleTabClick}
          >
            <i className="fas fa-search" />
            Available Players
          </NavLink>
          <NavLink
            to="/favorite-players"
            className="admin-sidebar__link"
            activeClassName="admin-sidebar__link--active"
            onClick={handleTabClick}
          >
            <i className="fas fa-bookmark" />
            Favorites
          </NavLink>
          <NavLink
            to="/recommendations"
            className="admin-sidebar__link"
            activeClassName="admin-sidebar__link--active"
          >
            <i className="fas fa-magic" />
            Recommended
          </NavLink>
          <NavLink
            to="/messages"
            className="admin-sidebar__link"
            activeClassName="admin-sidebar__link--active"
            onClick={() => { dispatch(setUnreadCount(0)); dispatch(setAdminUnreadCount(0)); }}
          >
            <i className="fas fa-envelope" />
            Messages
            {(unreadCount + adminUnreadCount) > 0 && (
              <span className="admin-sidebar__badge">{unreadCount + adminUnreadCount}</span>
            )}
          </NavLink>
        </nav>
        <div className="admin-sidebar__footer">
          <NavLink
            to="/finish-profile"
            className="admin-sidebar__link"
            activeClassName="admin-sidebar__link--active"
          >
            <i className="fas fa-cog" />
            Settings
          </NavLink>
          <button
            type="button"
            className="admin-sidebar__link admin-sidebar__link--logout"
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt" />
            Logout
          </button>
        </div>
      </div>
    </aside>
  );
};

export default CoachBar;
